import { computed, ref } from "vue";
import { defineStore } from "pinia";
import {
  GetGeoLocationsResponse,
  GetRoutingPolicyBySubscriptionIdResponse,
  JpdsReadinessResponse,
  PolicyDetailsGeoModel,
  type RoutingPolicyModel,
} from "@shared/types/common";
import {
  DnsRoutingGeoLocationSetupState,
  DnsRoutingJpdsTopologySetupState,
  DnsRoutingPolicy,
  DnsRoutingSingleRoutingUrlState,
  GeoLocationSetupItem,
} from "@shared/types/localtypes.ts";

export const useDnsRoutingStore = defineStore("dnsRouting", () => {
  // State
  const _policiesResponse = ref<GetRoutingPolicyBySubscriptionIdResponse>({
    policies: [],
  });
  const _geoLocationListResponse = ref<GetGeoLocationsResponse>({
    continents: [],
  });
  const _jpdsReadinessResponse = ref<JpdsReadinessResponse>({ status: [] });
  const _isLoadingPolicies = ref<boolean>(false);
  const _isLoadingGeoLocations = ref<boolean>(false);
  // Latches to true once readiness has resolved at least once, so callers can
  // tell "not ready" apart from "not known yet". Monotonic within a
  // subscription: polling must not reset it, only resetJpdsReadiness may.
  const _hasLoadedJpdsReadiness = ref<boolean>(false);
  const _singleRoutingUrlState = ref<DnsRoutingSingleRoutingUrlState>({
    singleUrlValue: "",
    isReadyForSending: false,
  });
  const _jpdsTopologySetupState = ref<DnsRoutingJpdsTopologySetupState>({
    primaryJpdTechServerName: "",
    secondaryJpdTechServerName: "",
    isReadyForSending: false,
  });
  const _geoLocationSetup = ref<DnsRoutingGeoLocationSetupState>({
    isReadyForSending: false,
    geoLocationSetup: [],
  });
  const _activePolicyTab = ref<DnsRoutingPolicy>("manual-failover");

  // Getters
  const policiesResponse = computed(() => _policiesResponse.value);
  const singleRoutingUrlState = computed(() => _singleRoutingUrlState.value);
  const jpdsTopologySetupState = computed(() => _jpdsTopologySetupState.value);
  const geoLocationSetup = computed(() => _geoLocationSetup.value);
  const geoLocationListResponse = computed(
    () => _geoLocationListResponse.value,
  );
  const jpdsReadinessResponse = computed(() => _jpdsReadinessResponse.value);
  const isLoadingPolicies = computed(() => _isLoadingPolicies.value);
  const hasLoadedJpdsReadiness = computed(() => _hasLoadedJpdsReadiness.value);
  const isLoadingGeoLocations = computed(() => _isLoadingGeoLocations.value);
  const activePolicyTab = computed(() => _activePolicyTab.value);

  // Actions
  const setRoutingPoliciesResponse = (
    newPoliciesResponse: GetRoutingPolicyBySubscriptionIdResponse,
  ) => {
    _policiesResponse.value = newPoliciesResponse;
  };

  const setSingleRoutingUrlState = (
    newSingleRoutingUrlState: DnsRoutingSingleRoutingUrlState,
  ) => {
    _singleRoutingUrlState.value = newSingleRoutingUrlState;
  };

  const setJpdsTopologySetupState = (
    newJpdsTopologySetupState: DnsRoutingJpdsTopologySetupState,
  ) => {
    _jpdsTopologySetupState.value = newJpdsTopologySetupState;
  };

  const setGeoLocationSetup = (
    newGeoLocationSetup: DnsRoutingGeoLocationSetupState,
  ) => {
    _geoLocationSetup.value = newGeoLocationSetup;
  };

  const setGeoLocationSetupIsReady = (isSetupReady: boolean) => {
    _geoLocationSetup.value.isReadyForSending = isSetupReady;
  };

  const setGeoLocationSetupForSpecificJpd = (
    policyDetailsGeoModel: PolicyDetailsGeoModel,
    index: number,
  ) => {
    _geoLocationSetup.value.geoLocationSetup[index] = {
      ..._geoLocationSetup.value.geoLocationSetup[index],
      ...policyDetailsGeoModel,
    };
  };

  const addJpdConfigToGeoLocationSetup = (
    newGeoLocationConfig: GeoLocationSetupItem,
  ) => {
    _geoLocationSetup.value.geoLocationSetup.push(newGeoLocationConfig);
  };

  const removeJpdGeoLocationSetup = (indexOfSetupToRemove: number) => {
    _geoLocationSetup.value.geoLocationSetup.splice(indexOfSetupToRemove, 1);
  };

  const setGeoLocationListResponse = (
    newGeoLocationListResponse: GetGeoLocationsResponse,
  ) => {
    _geoLocationListResponse.value = newGeoLocationListResponse;
  };

  const setJpdsReadinessResponse = (
    newJpdsReadinessResponse: JpdsReadinessResponse,
  ) => {
    _jpdsReadinessResponse.value = newJpdsReadinessResponse;
  };

  // Readiness is scoped to a single subscription. Both fields must be cleared
  // together, otherwise the latch would vouch for another account's data.
  const resetJpdsReadiness = () => {
    _jpdsReadinessResponse.value = { status: [] };
    _hasLoadedJpdsReadiness.value = false;
  };

  const updateOrCreatePolicyInStore = (newPolicy: RoutingPolicyModel) => {
    const policyModelIndex = _policiesResponse.value.policies.findIndex(
      (policy) => policy.routing_policy_id === newPolicy.routing_policy_id,
    );
    if (policyModelIndex === -1) {
      _policiesResponse.value.policies.push(newPolicy);
      return;
    }
    _policiesResponse.value.policies[policyModelIndex] = newPolicy;
  };

  const makeExistingPolicyInProgress = (routingPolicyId: string) => {
    const policyIndex = _policiesResponse.value.policies.findIndex(
      (policy) => policy.routing_policy_id === routingPolicyId,
    );
    if (policyIndex === -1) {
      return;
    }
    _policiesResponse.value.policies[policyIndex].policy_status = "in_progress";
  };

  const setIsLoadingPolicies = (newIsLoadingPolicies: boolean) => {
    _isLoadingPolicies.value = newIsLoadingPolicies;
  };

  const setHasLoadedJpdsReadiness = (newHasLoadedJpdsReadiness: boolean) => {
    _hasLoadedJpdsReadiness.value = newHasLoadedJpdsReadiness;
  };

  const setIsLoadingGeoLocations = (newIsLoadingGeoLocations: boolean) => {
    _isLoadingGeoLocations.value = newIsLoadingGeoLocations;
  };

  const setActivePolicyTab = (newActivePolicyTab: DnsRoutingPolicy) => {
    _activePolicyTab.value = newActivePolicyTab;
  };

  return {
    policiesResponse,
    singleRoutingUrlState,
    jpdsTopologySetupState,
    geoLocationSetup,
    geoLocationListResponse,
    jpdsReadinessResponse,
    isLoadingPolicies,
    hasLoadedJpdsReadiness,
    isLoadingGeoLocations,
    activePolicyTab,
    setRoutingPoliciesResponse,
    setSingleRoutingUrlState,
    setJpdsTopologySetupState,
    setGeoLocationSetup,
    setGeoLocationSetupIsReady,
    setGeoLocationSetupForSpecificJpd,
    addJpdConfigToGeoLocationSetup,
    removeJpdGeoLocationSetup,
    setGeoLocationListResponse,
    setJpdsReadinessResponse,
    resetJpdsReadiness,
    updateOrCreatePolicyInStore,
    makeExistingPolicyInProgress,
    setIsLoadingPolicies,
    setHasLoadedJpdsReadiness,
    setIsLoadingGeoLocations,
    setActivePolicyTab,
  };
});
